import { useEffect, useRef, useState } from 'react'

import { useReducedMotion } from './useReducedMotion'

interface Options {
  /** Fraction of the element that must be visible. */
  threshold?: number
  rootMargin?: string
}

/**
 * Flips `data-in` once an element scrolls into view, then disconnects.
 *
 * The stagger is done in CSS via `transition-delay: calc(var(--i) * 90ms)`, so
 * this hook only ever answers a boolean. Under reduce-motion it is true
 * immediately and the CSS delay collapses to zero.
 *
 * @returns a ref to attach, and whether the element has been revealed
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
}: Options = {}) {
  const ref = useRef<T | null>(null)
  const reduced = useReducedMotion()
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (reduced) {
      setInView(true)
      return
    }

    const el = ref.current
    if (!el) return

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }

    let delivered = false

    const observer = new IntersectionObserver(
      (entries) => {
        // Any callback at all proves the observer is delivering, so the
        // fail-safe below can stand down.
        delivered = true
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            observer.disconnect()
          }
        }
      },
      { threshold, rootMargin },
    )

    observer.observe(el)

    // Fail-safe. The hidden state is `opacity: 0` in CSS, so if the observer
    // is present but never delivers (headless renderers, exotic embeddings,
    // a browser bug), every section would stay invisible forever. Reveal
    // unconditionally if nothing has come back in a reasonable window.
    const failsafe = window.setTimeout(() => {
      if (!delivered) {
        setInView(true)
        observer.disconnect()
      }
    }, 1500)

    return () => {
      window.clearTimeout(failsafe)
      observer.disconnect()
    }
  }, [reduced, threshold, rootMargin])

  return { ref, inView }
}
